import { cn } from '@navinc/base-react-components'
import { Icon } from '@navinc/base-react-components/wayfinder'

import { cmsLinkFragment, CMSLinkFromFragment } from '@/app/_components/Link/index.jsx'
import { LexicalRichText, LexicalRichTextContent } from '@/app/_components/RichText/lexical.jsx'
import { FragmentType } from '@/app/lib/cms/index.js'

export const calloutBlockTypename = 'CmsCallout'

export type CalloutProps = {
  intent?: 'custom' | 'callToAction' | 'tip' | 'disclaimer' | 'link' | null
  emphasis?: 'standard' | 'primary' | 'important' | 'prime' | null
  iconName?: string | null
  heading?: string | null
  links?: { link?: FragmentType<typeof cmsLinkFragment> | null }[] | null
  richText?: LexicalRichTextContent
}

const emphasisClasses = {
  standard: 'bg-surface text-onSurface border border-outlineVariant',
  primary: 'bg-primaryContainer text-onPrimaryDim',
  important: 'bg-primary text-inverseOnSurface',
  prime: 'bg-superContainer text-onSuperContainer',
}

const intentClasses = {
  custom: '',
  callToAction: '',
  tip: 'bg-[#EDECFC] text-onPrimaryContainer',
  disclaimer: emphasisClasses.standard,
  link: emphasisClasses.primary,
}

const icons = {
  disclaimer: 'info',
  tip: 'lightbulb',
  link: 'arrow_outward',
}

export const Callout = ({ intent, emphasis, iconName, heading, richText, links }: CalloutProps) => {
  let calloutIcon: string | null | undefined
  if (intent === 'custom') {
    calloutIcon = iconName
  } else if (intent && intent !== 'callToAction') {
    calloutIcon = icons[intent]
  }

  return (
    <div
      className={cn(
        'p-300 flex items-start rounded-200 gap-100',
        emphasisClasses[emphasis ?? 'standard'],
        intentClasses[intent ?? 'callToAction'],
      )}
    >
      {calloutIcon && <Icon name={calloutIcon} size={heading ? 'large' : 'medium'} color="inherit" className="mt-px" />}
      <div>
        {heading && <p className="title2-emphasized mb-200">{heading}</p>}
        {intent === 'disclaimer' && <p className="font-bold">Disclaimer</p>}
        {intent === 'tip' && <p className="font-bold">Nav Tip</p>}
        {richText && <LexicalRichText content={richText} />}
        {links && links.length ? (
          <div className={cn('relative z-30 mt-300 flex gap-200')}>
            {links.map(({ link }, i) => {
              if (!link) return null

              return (
                <CMSLinkFromFragment
                  data={link}
                  key={i}
                  density="standard"
                  className="w-full md:w-auto"
                  data-testid={`callout:cta-button-${i}`}
                />
              )
            })}
          </div>
        ) : null}
      </div>
    </div>
  )
}
